import React from 'react';
import { ArrowRightIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const OPTIONS = [
  {
    key: 'one_to_three',
    years: '1-3',
    label: 'Near Term',
    description: 'Planning and preparation',
  },
  {
    key: 'four_to_six',
    years: '4-6',
    label: 'Mid Term',
    description: 'Strategic positioning',
  },
  {
    key: 'seven_plus',
    years: '7+',
    label: 'Long Term',
    description: 'Flexibility and growth',
  },
];

export default function StepTimeline({
  value,
  onChange,
  onBack,
  onSubmit,
  saving = false,
  approvalRequired = false,
  approvalConfirmation = false,
}) {
  // Post-submit confirmation when manager approval is required. The LaunchPad's
  // existing ApprovalStatusBanner will pick up the pending state on next render,
  // so the modal just needs to acknowledge the request and close on dismiss.
  if (approvalConfirmation) {
    return (
      <div className="w-full px-8 py-12 sm:px-12 sm:py-16">
        <div className="w-full max-w-2xl mx-auto text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
            <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-3xl sm:text-4xl font-semibold text-gray-900 mb-4 tracking-tight">
            Request sent for approval
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Your manager has been notified. We&apos;ll let you know once your request has been reviewed. You can continue using the app in the meantime.
          </p>
          <button
            onClick={() => { if (typeof window !== 'undefined') window.location.reload(); }}
            className="inline-flex items-center justify-center px-8 py-3 text-white font-medium rounded-full min-w-[12rem]"
            style={{ backgroundColor: 'var(--succession-primary, var(--company-primary, #5489B6))' }}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full px-8 py-12 sm:px-12 sm:py-16">
      <div className="w-full max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-semibold text-gray-900 mb-4 tracking-tight">
            When do you plan to retire?
          </h2>
          <p className="text-lg text-gray-500">Select your timeline</p>
          {approvalRequired && (
            <p className="mt-4 text-sm text-gray-500 italic">
              Note: changes to your motivation require manager approval. Your request will be sent for review on submit.
            </p>
          )}
        </div>

        {/* Timeline Visual */}
        <div className="mb-16">
          {/* Timeline Line */}
          <div className="relative mb-8">
            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-200 -translate-y-1/2" />

            <div className="relative flex justify-between items-center">
              {OPTIONS.map((opt, index) => {
                const selected = value === opt.key;
                return (
                  <button
                    key={opt.key}
                    onClick={() => onChange(opt.key)}
                    className="relative group flex flex-col items-center"
                  >
                    {/* Timeline Dot */}
                    <div
                      className={`w-16 h-16 rounded-full border-4 bg-white transition-all duration-300 flex items-center justify-center mb-4 z-10
                        ${
                          selected
                            ? 'shadow-lg scale-110'
                            : 'border-gray-300 group-hover:border-gray-400 group-hover:scale-105'
                        }`}
                      style={selected ? {
                        borderColor: 'var(--succession-primary, var(--company-primary, #5489B6))'
                      } : {}}
                    >
                      <span
                        className={`text-xl font-bold transition-colors duration-300
                          ${selected ? 'text-gray-900' : 'text-gray-400 group-hover:text-gray-600'}`}
                      >
                        {opt.years}
                      </span>
                    </div>

                    {/* Label */}
                    <div className="text-center">
                      <p
                        className={`text-base font-semibold mb-1 transition-colors duration-300
                          ${selected ? 'text-gray-900' : 'text-gray-600 group-hover:text-gray-800'}`}
                      >
                        {opt.label}
                      </p>
                      <p
                        className={`text-sm transition-colors duration-300 max-w-[120px]
                          ${selected ? 'text-gray-600' : 'text-gray-400 group-hover:text-gray-500'}`}
                      >
                        {opt.description}
                      </p>
                    </div>

                    {/* Selection Indicator */}
                    {selected && (
                      <div className="absolute -bottom-3 left-1/2 -translate-x-1/2">
                        <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: 'var(--succession-primary, var(--company-primary, #5489B6))' }} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Years Label */}
          <div className="text-center text-sm text-gray-400 font-medium">
            YEARS UNTIL RETIREMENT
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center max-w-2xl mx-auto">
          <button
            onClick={onBack}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-3 text-gray-600 font-medium hover:text-gray-900 transition-colors duration-200 group disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
            Back
          </button>
          <button
            disabled={!value || saving}
            onClick={onSubmit}
            aria-busy={saving}
            className="inline-flex items-center justify-center gap-2 px-8 py-3 text-white font-medium disabled:cursor-not-allowed transition-all duration-200 group rounded-full min-w-[12rem]"
            style={{
              backgroundColor: 'var(--succession-primary, var(--company-primary, #5489B6))',
              opacity: !value ? 0.3 : 1,
            }}
            onMouseEnter={(e) => {
              if (!e.currentTarget.disabled) {
                e.currentTarget.style.opacity = '0.9';
              }
            }}
            onMouseLeave={(e) => {
              if (!e.currentTarget.disabled) {
                e.currentTarget.style.opacity = '1';
              }
            }}
          >
            {saving ? (
              <>
                <svg
                  className="w-4 h-4 animate-spin"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3" />
                  <path d="M22 12a10 10 0 0 1-10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
                <span>{approvalRequired ? 'Sending request…' : 'Saving…'}</span>
              </>
            ) : (
              <>
                <span>{approvalRequired ? 'Send for approval' : 'Complete'}</span>
                <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}


